import { UMB_COLLECTION_CONTEXT, type UmbDefaultCollectionContext } from '../../default/index.js';
import type { UUIPaginationEvent } from '@umbraco-cms/backoffice/external/uui';
import { UmbTextStyles } from '@umbraco-cms/backoffice/style';
import { css, html, customElement, nothing, state } from '@umbraco-cms/backoffice/external/lit';
import { UmbLitElement } from '@umbraco-cms/backoffice/lit-element';

@customElement('umb-collection-pagination')
export class UmbCollectionPaginationElement extends UmbLitElement {
	@state()
	private _totalPages = 1;

	@state()
	private _currentPage = 1;

	@state()
	private _totalItems = 0;

	@state()
	private _currentItemsCount = 0;

	private _collectionContext?: UmbDefaultCollectionContext<any, any>;

	constructor() {
		super();
		this.consumeContext(UMB_COLLECTION_CONTEXT, (instance) => {
			this._collectionContext = instance;
			this.#observeCurrentPage();
			this.#observerTotalPages();
			this.#observeTotalItems();
			this.#observeItems();
		});
	}

	#observeCurrentPage() {
		this.observe(
			this._collectionContext?.pagination.currentPage,
			(currentPage) => {
				this._currentPage = currentPage ?? 1;
			},
			'umbCurrentPageObserver',
		);
	}

	#observerTotalPages() {
		this.observe(
			this._collectionContext?.pagination.totalPages,
			(totalPages) => {
				this._totalPages = totalPages ?? 1;
			},
			'umbTotalPagesObserver',
		);
	}

	#observeTotalItems() {
		this.observe(
			this._collectionContext?.totalItems,
			(totalItems) => {
				this._totalItems = totalItems ?? 0;
			},
			'umbTotalItemsObserver',
		);
	}

	#observeItems() {
		this.observe(
			this._collectionContext?.items,
			(items) => {
				this._currentItemsCount = items?.length ?? 0;
			},
			'umbItemsObserver',
		);
	}

	#onChange(event: UUIPaginationEvent) {
		this._collectionContext?.pagination.setCurrentPageNumber(event.target.current);
	}

	override render() {
		if (this._currentItemsCount === this._totalItems || this._totalPages <= 1) {
			return nothing;
		}

		return html`<uui-pagination
			.current=${this._currentPage}
			.total=${this._totalPages}
			firstlabel=${this.localize.term('general_first')}
			previouslabel=${this.localize.term('general_previous')}
			nextlabel=${this.localize.term('general_next')}
			lastlabel=${this.localize.term('general_last')}
			@change=${this.#onChange}></uui-pagination>`;
	}

	static override styles = [
		UmbTextStyles,
		css`
			:host {
				display: contents;
			}

			uui-pagination {
				margin-top: var(--uui-size-space-3);
			}
		`,
	];
}

declare global {
	interface HTMLElementTagNameMap {
		'umb-collection-pagination': UmbCollectionPaginationElement;
	}
}
